import { SubAccountStates } from '@generated/graphql';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import { isSubAccount } from '@interfaces/portfolioReport';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import { formatAuditAccountType } from '../presentation/accountTypeLabels';

/** Active sleeves with balance or trade activity — excludes inactive / untouched accounts. */
export const getAuditActiveSubAccountResults = (
  audit: OptimizerAuditV2Record | undefined,
  report?: PortfolioReport,
) => {
  if (!audit) {
    return [];
  }

  const inactiveSubAccountIds = new Set(
    (report?.subAccounts ?? [])
      .map((entry) => entry.subAccount)
      .filter((subAccount) => subAccount?.state === SubAccountStates.Inactive)
      .map((subAccount) => subAccount!.id)
      .filter(Boolean),
  );

  return (audit.subAccountResults ?? []).filter((result) => {
    if (result.subAccountId && inactiveSubAccountIds.has(result.subAccountId)) {
      return false;
    }

    const hasTradeActivity = (result.weightDiffs ?? []).some(
      (diff) => Math.abs(diff.deltaCents ?? 0) > 0,
    );
    const hasBalance = (result.inputMarketValueCents ?? 0) !== 0;

    return hasBalance || hasTradeActivity;
  });
};

export const getAuditAccountTypeLabels = (
  audit: OptimizerAuditV2Record | undefined,
  report?: PortfolioReport,
): string[] => {
  const labels = [
    ...new Set(
      getAuditActiveSubAccountResults(audit, report)
        .map((result) => result.accountType)
        .filter(Boolean)
        .map((accountType) => formatAuditAccountType(accountType)),
    ),
  ];

  if (labels.length > 0) {
    return labels;
  }

  if (report && isSubAccount(report.object)) {
    const accountType = report.object.account?.type;

    if (accountType) {
      return [formatAuditAccountType(accountType)];
    }
  }

  return [];
};
